/**
 * Post-processing pipeline.
 *
 * - Render scene to a render target
 * - UnrealBloomPass for emissive accents (stage lights, LED towers, lasers)
 * - Output pass with tone mapping passthrough
 *
 * Tuned conservative so it doesn't blur the whole world.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

export class PostFX {
  readonly composer: EffectComposer;
  readonly bloom: UnrealBloomPass;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera
  ) {
    this.composer = new EffectComposer(renderer);
    this.composer.addPass(new RenderPass(scene, camera));

    // Punchier bloom — flags, LED towers, and stage trim should clearly glow.
    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.85, // strength
      0.75, // radius
      0.55  // threshold — let mid-bright emissives through
    );
    this.composer.addPass(this.bloom);

    this.composer.addPass(new OutputPass());
  }

  resize(w: number, h: number): void {
    this.composer.setSize(w, h);
    this.bloom.setSize(w, h);
  }

  render(): void {
    this.composer.render();
  }
}
